import { useQueryClient } from '@tanstack/react-query';
import type { DashboardT } from 'business/definitions';
import { trackEvent } from 'helpers/trackerHelpers';
import { useAPICreateChart } from 'modules/decision/api/dashboard/useAPICreateChart';
import { getChartData } from 'modules/decision/pages/Explore/hooks/reducers/helpers';
import type { SettingsState } from 'modules/decision/pages/Explore/hooks/reducers/types';
import { useSaveChartToDashboard } from 'modules/decision/pages/Explore/hooks/saveChartToDashboard';
import { ACTIVE_DASHBOARDS_QUERY_KEY } from 'shared/api/getDashboardsList';
import { useAPIDuplicateWidget } from 'shared/api/useAPIDuplicateWidget';

export const useAddChartToDashboard = () => {
    const { saveChartFromData } = useAPICreateChart();
    const { saveChartToDashboard } = useSaveChartToDashboard();
    const queryClient = useQueryClient();

    const updateDashboardOnSuccess = (
        key: number,
        updatedProps: Partial<DashboardT>,
    ) => {
        queryClient.setQueryData(
            [ACTIVE_DASHBOARDS_QUERY_KEY],
            (oldDashboards: DashboardT[]) =>
                oldDashboards.map((dashboard) =>
                    dashboard.key === key
                        ? { ...dashboard, updatedProps }
                        : dashboard,
                ),
        );
    };

    const duplicateWidgetMutation = useAPIDuplicateWidget(
        updateDashboardOnSuccess,
    );

    const addChartToDashboard = (
        explore: ExploreQueryT,
        dashboardElement?: DashboardT,
        chartKey?: number | null,
        widgetKey?: number | null,
        isInDashboard?: boolean,
        query?: ExploreQueryT,
        settings?: Pick<
            SettingsState,
            'chartTitle' | 'selectedViz' | 'chartSettings'
        >,
        redirect: boolean = false,
        snackAction: boolean = true,
        fromPosition?: FromPositionQueryT,
    ) => {
        if (dashboardElement && dashboardElement.key) {
            const { key, slug } = dashboardElement;
            if (isInDashboard && widgetKey) {
                duplicateWidgetMutation.mutate({
                    widgetKey,
                    dashboardSlug: slug,
                    fromPosition,
                });
            } else if (chartKey !== undefined && chartKey !== null) {
                saveChartToDashboard({
                    dashboard: { key, slug },
                    explore,
                    redirect,
                    snackAction,
                    fromPosition,
                });
            } else if (query !== undefined && settings !== undefined) {
                saveChartFromData({
                    dashboard: { key, slug },
                    chartData: getChartData(query, settings),
                    redirect,
                    snackAction,
                    fromPosition,
                });
                trackEvent(
                    'decision/indicatorlibrary/opendata-and-models/saved-to-dashboard',
                    {
                        indicator: chartKey,
                    },
                );
            }
        }
    };
    return { addChartToDashboard };
};
